/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

export default styled(Box)(({ theme, ownerState }) => {
  const { palette, typography, functions, boxShadows } = theme;
  const { darkMode } = ownerState;

  const { white, dark, light, grey, gradients, info, error, warning, success } = palette;
  const { size, fontWeightMedium, fontWeightBold, fontWeightRegular, fontWeightLight } = typography;
  const { linearGradient, pxToRem } = functions;
  const { md } = boxShadows;

  return {
    height: "100%",
    position: "relative",
    borderRadius: pxToRem(8),
    overflow: "hidden",
    backgroundColor: darkMode ? dark.main : white.main,
    boxShadow: `${md}`,

    "& .fc-media-screen": {
      height: "100%",
      color: dark.main,
    },

    "& .fc-theme-standard .fc-scrollgrid": {
      border: "none",
    },

    "& .fc-theme-standard thead tr th": {
      borderLeft: "none",
      borderRight: "none",
    },

    "& .fc-theme-standard td, .fc-theme-standard th": {
      borderColor: light.main,
    },

    "& .fc th": {
      textAlign: "center",
    },

    "& .fc .fc-col-header-cell-cushion": {
      fontSize: size.sm,
      fontWeight: fontWeightMedium,
      color: darkMode ? white.main : grey[500],
    },

    "& .fc .fc-daygrid-day-number": {
      color: darkMode ? white.main : grey[700],
      fontSize: size.sm,
      fontWeight: fontWeightLight,
      width: "100%",
      textAlign: "center",
    },

    "& .fc-day-sun, & .fc-day-sat, & .calendar-tuesday": {
      backgroundColor: darkMode ? "rgba(255, 193, 7, 0.08)" : "rgba(255, 193, 7, 0.12)",
    },

    "& .fc-scrollgrid-section.fc-scrollgrid-section-header > td": {
      border: "none",
    },

    "& .fc-scrollgrid-section.fc-scrollgrid-section-body.fc-scrollgrid-section-liquid > td": {
      border: "none",
    },

    "& .fc-scrollgrid-sync-table": {
      height: "auto !important",
    },

    "& .fc .fc-view-harness-active > .fc-view": {
      position: "static",
      height: "100%",
    },

    "& .fc .fc-scroller-liquid-absolute": {
      position: "static",
    },

    "& .fc-daygrid-event": {
      margin: `${pxToRem(0.5)} ${pxToRem(2)}`,
      border: "none",
      borderRadius: pxToRem(5.6),
      cursor: "pointer",
      fontSize: size.sm,
      fontWeight: fontWeightMedium,
      paddingLeft: pxToRem(4),
      transition: "transform 150ms ease, box-shadow 150ms ease",

      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: `${md}`,
      },
    },

    "& .fc .fc-daygrid-event.event-rahu-daytime, & .fc .fc-list-event.event-rahu-daytime td": {
      backgroundColor: "#fde8e7 !important",
      color: "#8f302d !important",
      borderColor: "#efb8b5 !important",
      marginBottom: pxToRem(4),
    },

    "& .fc .fc-daygrid-event.event-rahu-nighttime, & .fc .fc-list-event.event-rahu-nighttime td": {
      backgroundColor: "#e4f1ff !important",
      color: "#164b7a !important",
      borderColor: "#9dc7ed !important",
      marginBottom: pxToRem(4),
    },

    "@media (min-width: 601px)": {
      "& .fc-daygrid-event .fc-daygrid-event-dot": {
        marginLeft: pxToRem(4),
        marginRight: pxToRem(4),
      },
    },

    "& .fc .fc-daygrid-body-unbalanced .fc-daygrid-day-events": {
      minHeight: pxToRem(32),
    },

    "& .fc-event-title": {
      fontSize: `${size.xs} !important`,
      fontWeight: `${fontWeightRegular} !important`,
      padding: `${pxToRem(2)} ${pxToRem(4.8)} ${pxToRem(1.5)} !important`,
      whiteSpace: "pre-line",
      lineHeight: 1.25,
    },

    "& .calendar-event-content": {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: pxToRem(4),
      width: "100%",
    },

    "& .calendar-event-label": {
      whiteSpace: "pre-line",
      flex: 1,
    },

    "& .calendar-event-time, & .calendar-event-name": {
      display: "block",
    },

    "& .calendar-toolbar-hint": {
      display: "none",
    },

    "& .calendar-event-info": {
      flexShrink: 0,
      fontSize: `${size.sm} !important`,
      marginTop: pxToRem(2),
      marginRight: pxToRem(4),
      opacity: 1,
    },

    "& .fc-list-event-title": {
      whiteSpace: "pre-line",
      lineHeight: 1.35,
    },

    "& .fc-button, .fc-today-button, .fc-button:disabled": {
      backgroundColor: `${info.main} !important`,
      borderColor: `${info.main} !important`,
      fontSize: `${size.sm} !important`,
      boxShadow: `${md} !important`,
      opacity: "1 !important",
      transition: `all 150ms ease-in`,

      "&:hover, &:focus, &:active": {
        transform: "scale(1.02)",
        boxShadow: `${md} !important`,
        backgroundColor: `${info.main} !important`,
        borderColor: `${info.main} !important`,
      },
    },

    "& .fc-dayGridMonth-button, & .fc-listMonth-button": {
      backgroundColor: "transparent !important",
      border: `1px solid ${info.main} !important`,
      boxShadow: "none !important",
      color: `${info.main} !important`,

      "&:hover, &:focus, &:active, &.fc-button-active": {
        backgroundColor: `${info.main} !important`,
        borderColor: `${info.main} !important`,
        boxShadow: "none !important",
        color: `${white.main} !important`,
      },
    },

    "& .fc-prev-button, & .fc-next-button": {
      backgroundColor: "transparent !important",
      border: "none !important",
      boxShadow: "none !important",
      color: `${warning.main} !important`,
      minWidth: pxToRem(36),
      padding: `${pxToRem(4)} 0 !important`,
      textDecoration: "underline",
      textUnderlineOffset: pxToRem(3),

      "& .fc-icon": {
        color: `${warning.main} !important`,
        fontSize: `${size.lg} !important`,
      },

      "&:hover, &:focus, &:active, &:disabled": {
        backgroundColor: "transparent !important",
        border: "none !important",
        boxShadow: "none !important",
        color: `${warning.main} !important`,
        textDecoration: "underline",
      },
    },

    "& .fc .fc-button .fc-icon": {
      fontSize: size.sm,
    },

    "& .fc-toolbar-chunk .fc-button-group": {
      gap: pxToRem(6),
    },

    "& .fc-toolbar-chunk .fc-button-group > .fc-button": {
      borderRadius: pxToRem(5.6),
    },

    "& .fc-header-toolbar": {
      display: "flex",
      flexWrap: "nowrap",
      alignItems: "center",
      gap: pxToRem(8),
      backgroundColor: darkMode ? grey[800] : grey[100],
      borderRadius: pxToRem(6),
      padding: `${pxToRem(8)} ${pxToRem(10)}`,
    },

    "& .fc-header-toolbar .fc-toolbar-chunk": {
      display: "flex",
      flexWrap: "nowrap",
      alignItems: "center",
      gap: pxToRem(6),
    },

    "& .fc-list-day-cushion": {
      backgroundColor: darkMode ? dark.main : grey[100],
      borderTop: `3px solid ${warning.main}`,
      borderBottom: `1px solid ${light.main}`,
      color: darkMode ? white.main : dark.main,
      fontSize: `${size.sm} !important`,
      fontWeight: `${fontWeightBold} !important`,
      padding: `${pxToRem(10)} ${pxToRem(12)} !important`,
    },

    "& .fc-list-event:hover td": {
      backgroundColor: darkMode ? grey[800] : grey[100],
    },

    "& .fc-list-event:hover .fc-list-event-title, & .fc-list-event:hover .fc-list-event-title *, & .fc-list-event:hover .calendar-event-label": {
      color: darkMode ? `${white.main} !important` : `${dark.main} !important`,
    },

    "& .fc-list-event td": {
      borderBottom: `1px solid ${light.main}`,
      cursor: "pointer",
      paddingTop: `${pxToRem(8)} !important`,
      paddingBottom: `${pxToRem(8)} !important`,
    },

    "& .fc-list-event-dot": {
      borderColor: `${warning.main} !important`,
    },

    "& .fc-daygrid-event-dot": {
      borderColor: `${warning.main} !important`,
    },

    "@media (hover: none)": {
      "& .fc-daygrid-event, & .fc-list-event td": {
        cursor: "default",
        WebkitTapHighlightColor: "transparent",
      },

      "& .fc-daygrid-event:active, & .fc-list-event:active td": {
        opacity: 0.82,
        transform: "scale(0.99)",
      },
    },

    "@media (max-width: 600px)": {
      "& .fc-header-toolbar": {
        gap: pxToRem(3),
        padding: `${pxToRem(6)} ${pxToRem(4)}`,
        flexWrap: "wrap",
      },

      "& .fc-header-toolbar .fc-toolbar-chunk": {
        gap: pxToRem(3),
      },

      "& .fc-header-toolbar .fc-toolbar-chunk:last-child": {
        flexBasis: "100%",
        justifyContent: "center",
      },

      "& .calendar-toolbar-hint": {
        display: "block",
        position: "absolute",
        top: pxToRem(88),
        left: pxToRem(8),
        right: pxToRem(8),
        zIndex: 3,
        margin: `${pxToRem(8)} 0 ${pxToRem(1)}`,
        padding: `${pxToRem(2)} ${pxToRem(8)}`,
        borderRadius: pxToRem(4),
        backgroundColor: darkMode ? "rgba(33, 150, 243, 0.18)" : "rgba(33, 150, 243, 0.12)",
        color: darkMode ? white.main : info.main,
        fontSize: `${pxToRem(10)} !important`,
        textAlign: "center",
      },

      "& .fc-view-harness": {
        marginTop: pxToRem(20),
      },

      "& .fc-prev-button, & .fc-next-button, & .fc-toolbar-title": {
        fontSize: `${size.xs} !important`,
      },

      "& .fc-prev-button, & .fc-next-button": {
        minWidth: pxToRem(28),
        padding: `${pxToRem(2)} 0 !important`,
      },

      "& .fc-event-title, & .calendar-event-label, & .fc-list-event-title": {
        fontSize: `${pxToRem(11)} !important`,
        lineHeight: 1.2,
      },

      "& .calendar-event-info": {
        fontSize: `${pxToRem(12)} !important`,
      },

      "& .fc-daygrid-view .calendar-event-name": {
        display: "none",
      },
    },

    "& .fc-toolbar-title": {
      fontSize: `${size.lg} !important`,
      fontWeight: `${fontWeightBold} !important`,
      color: darkMode ? white.main : dark.main,
      whiteSpace: "nowrap",
      margin: "0 !important",
    },

    "& .event-primary": {
      backgroundImage: linearGradient(gradients.primary.main, gradients.primary.state),
      "& *": { color: white.main },
    },

    "& .event-secondary": {
      backgroundImage: linearGradient(gradients.secondary.main, gradients.secondary.state),
      "& *": { color: white.main },
    },

    "& .event-info": {
      backgroundImage: linearGradient(gradients.info.main, gradients.info.state),
      "& *": { color: white.main },
    },

    "& .event-success": {
      backgroundColor: `${success.dark} !important`,
      backgroundImage: "none",
        "& *": { color: white.main },
    },

    "& .event-warning": {
      backgroundImage: linearGradient(gradients.warning.main, gradients.warning.state),
      "& *": { color: white.main },
    },

    "& .event-error": {
      backgroundImage: linearGradient(gradients.error.main, gradients.error.state),
      "& *": { color: white.main },
    },

    "& .event-light": {
      backgroundImage: linearGradient(gradients.light.main, gradients.light.state),

      "& *": { color: dark.main },
    },

    "& .event-dark": {
      backgroundImage: linearGradient(gradients.dark.main, gradients.dark.state),
      "& *": { color: white.main },
    },
  };
});
